import { useEffect, useState } from "react";

interface ShareDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
}

export default function ShareDialog({ open, onClose, title }: ShareDialogProps) {
  const [copyState, setCopyState] = useState<"idle" | "success" | "error">("idle");

  useEffect(() => {
    if (open) setCopyState("idle");
  }, [open]);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    if (open) document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopyState("success");
    } catch {
      setCopyState("error");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground-950/60 px-4 animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label="分享说明"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-lg bg-background-50"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-background-200 px-5 py-4">
          <div>
            <h2 className="text-base font-bold text-foreground-950">分享《{title}》</h2>
            <p className="mt-1 text-xs text-foreground-500">当前为演示状态，未接入第三方分享服务</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭"
            className="flex h-8 w-8 items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-900 cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="px-5 py-5">
          <p className="text-sm leading-7 text-foreground-700">
            本站不提供真实的社交平台分享接口。你可以复制当前页面链接，手动分享给朋友；页面内的分享按钮仅作交互演示。
          </p>

          <div className="mt-4 flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2.5">
            <i className="ri-link text-foreground-500" />
            <span className="min-w-0 flex-1 truncate text-xs text-foreground-600">
              {typeof window !== "undefined" ? window.location.href : ""}
            </span>
          </div>

          {copyState === "success" && (
            <p className="mt-3 flex items-center gap-1.5 text-xs text-primary-700">
              <i className="ri-check-line" />
              已复制当前页面链接
            </p>
          )}
          {copyState === "error" && (
            <p className="mt-3 flex items-center gap-1.5 text-xs text-accent-700">
              <i className="ri-error-warning-line" />
              复制失败，请手动复制浏览器地址栏中的链接
            </p>
          )}

          <div className="mt-5 flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={handleCopy}
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-file-copy-line" />
              复制页面链接
            </button>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex flex-1 items-center justify-center rounded-md border border-background-300 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-100 cursor-pointer whitespace-nowrap"
            >
              关闭
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}